/*******************************************************************************
 * ADOBE CONFIDENTIAL
 * ___________________
 *
 * Copyright 2026 Adobe
 * All Rights Reserved.
 *
 * NOTICE: All information contained herein is, and remains
 * the property of Adobe and its suppliers, if any. The intellectual
 * and technical concepts contained herein are proprietary to Adobe
 * and its suppliers and are protected by all applicable intellectual
 * property laws, including trade secret and copyright laws.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Adobe.
 ******************************************************************************/

import { useCallback, useState } from "react";
import * as React from "react";
import { useCurrentUser } from "actions/CurrentUserActions";
import upgradeIcon from "components/shared/images/upgrade-icon.svg";
import accessIcon from "components/shared/images/premium-black-and-white.svg";
import { useIsMobile } from "components/shared/Styles";
import {
  Analytics,
  AnalyticsEvent,
  ExpressCheckoutButtonPosition,
} from "constants/Analytics";
import { UpgradeExpressButton } from "components/PurchaseExpress";
import { useLocation } from "react-router-dom";
import { ProductFeatureList } from "components/Plans/ProductList";
import { AppPath } from "components/AppPath";
import { Icon } from "components/shared/Icon";
import { UpgradeModal } from "components/shared/UpgradeModal";

const PremiumButton: React.FC<{
  onClick: VoidFunction;
}> = ({ onClick }) => {
  const isMobile = useIsMobile();

  const buttonSize = isMobile ? "xl" : "l";

  return (
    <overlay-trigger placement="bottom" slot="trigger" triggered-by="click">
      <sp-button
        size={buttonSize}
        variant="secondary"
        slot="trigger"
        data-testid="premium-access-button"
        onClick={onClick}
      >
        <Icon aria-hidden="true" src={accessIcon} />
        {I18n.t("access_button.premium")}
      </sp-button>

      {
        // This is breaking on mobile.
        !isMobile && (
          <sp-tooltip slot="hover-content" open>
            {I18n.t("access_button.premium_tooltip")}
          </sp-tooltip>
        )
      }
    </overlay-trigger>
  );
};

export const UpgradeButtonCallbackOnly: React.FC<{
  onClick: (skippedToCheckout: boolean) => void;
  position: ExpressCheckoutButtonPosition;
}> = ({ onClick, position }) => {
  const isMobile = useIsMobile();
  const buttonSize = isMobile ? "xl" : "l";
  const { currentUser } = useCurrentUser();
  const isOnPlansPage = useLocation().pathname.endsWith(AppPath.PLANS);

  const onUpgradeButtonClicked = (skippedToCheckout: boolean = false): void => {
    Analytics.instance.logEvent(AnalyticsEvent.USER_CLICKS_UPGRADE_BUTTON, {
      position: position,
    });
    onClick(skippedToCheckout);
  };

  if (!currentUser) {
    return <></>;
  }

  if (currentUser?.hasPremiumAccess) {
    return <PremiumButton onClick={() => onClick(false)} />;
  } else if (isOnPlansPage) {
    // TODO: This is a pre-existing hack. The plans page logic/button should probably move into the Plans header.
    return (
      <UpgradeExpressButton
        color={"black"}
        position={
          isMobile
            ? ExpressCheckoutButtonPosition.FEATURES_PROFILE_DROPDOWN
            : ExpressCheckoutButtonPosition.FEATURES_HEADER
        }
        onClick={() => onUpgradeButtonClicked(true)}
      >
        <Icon aria-hidden="true" src={upgradeIcon} />
        {I18n.t("access_button.upgrade")}
      </UpgradeExpressButton>
    );
  } else {
    return (
      <sp-button
        size={buttonSize}
        variant="primary"
        treatment="fill"
        onClick={() => onUpgradeButtonClicked(false)}
      >
        <Icon aria-hidden="true" src={upgradeIcon} />
        {I18n.t("access_button.upgrade")}
      </sp-button>
    );
  }
};

// TODO: Once the Studio Header is refactored to use UpgradeButtonCallbackOnly, this can be removed
//       and we can rename UpgradeButtonCallbackOnly to UpgradeButton.
export const UpgradeButton: React.FC<{
  position: ExpressCheckoutButtonPosition;
  products?: ProductFeatureList[];
  productImage?: React.ReactNode;
}> = ({ position, products, productImage }) => {
  const [isOpen, setIsOpen] = useState(false);

  const handleClosed = useCallback((): void => {
    setIsOpen(false);
  }, []);

  const onUpgradeButtonClicked = (skippedToCheckout: boolean): void => {
    !skippedToCheckout && setIsOpen(true);
  };

  return (
    <>
      <UpgradeButtonCallbackOnly
        onClick={onUpgradeButtonClicked}
        position={position}
      />
      <UpgradeModal
        open={isOpen}
        onClose={handleClosed}
        position={position}
        products={products}
        productImage={productImage}
      />
    </>
  );
};
